import React, { useState, useEffect } from 'react';
import { useBilling } from '../hooks/useBilling';
import { useAuth } from '../hooks/useAuth';
import JobsTable from './JobsTable';
import BillingHistory from './BillingHistory';
import PaymentMethodModal from './PaymentMethodModal';
import AddFundsModal from './AddFundsModal';
import './AccountPage.css';

const AccountPage: React.FC = () => {
  const { apiKey } = useAuth();
  const { 
    balance, 
    getMonthlyUsage
  } = useBilling();
  
  const [showPaymentMethod, setShowPaymentMethod] = useState(false);
  const [showAddFunds, setShowAddFunds] = useState(false);
  const [activeTab, setActiveTab] = useState<'jobs' | 'transactions'>('jobs');
  const [monthlyUsage, setMonthlyUsage] = useState<{ amount: number; contacts: number }>({ amount: 0, contacts: 0 });

  useEffect(() => {
    const loadMonthlyUsage = async () => {
      try {
        console.log('🔄 Loading monthly usage...');
        
        // Use the new getMonthlyUsage function from the billing hook
        const usageData = await getMonthlyUsage();
        
        if (usageData) {
          console.log('✅ Monthly usage loaded:', usageData);
          setMonthlyUsage(usageData);
        } else {
          console.log('⚠️ Monthly usage calculation failed.');
        }
      } catch (error) {
        console.error('❌ Error loading monthly usage:', error);
      }
    };

    loadMonthlyUsage();
  }, [apiKey, balance, getMonthlyUsage]);

  const needsPaymentMethod = !balance?.has_payment_method;

  return (
    <div className="account-page">
      {/* Account Overview Section */}
      <div className="account-overview">
        <div className="overview-card payment-card">
          <div className="card-content">
            <h3>PAYMENT METHOD</h3>
            <div className="card-status">
              {needsPaymentMethod ? (
                <>
                  <span className="status-inactive">Inactive</span>
                  <button 
                    className="update-button"
                    onClick={() => setShowPaymentMethod(true)}
                  >
                    Set Payment Method
                  </button>
                </>
              ) : (
                <>
                  <span className="status-active">Active</span>
                  <button 
                    className="update-button"
                    onClick={() => setShowPaymentMethod(true)}
                  >
                    Update
                  </button>
                </>
              )}
            </div>
            <div className="pricing-info">
              Pay-as-you-go pricing: $0.02 per contact
            </div>
          </div>
        </div>

        <div className="overview-card usage-card">
          <div className="card-content">
            <h3>USAGE THIS MONTH</h3>
            <div className="usage-amount">${monthlyUsage.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            <div className="usage-contacts">
              {monthlyUsage.contacts.toLocaleString()} contacts
            </div>
          </div>
        </div>
      </div>

      {/* Tabbed Interface */}
      <div className="account-tabs">
        <div className="tab-navigation">
          <button 
            className={`tab-button ${activeTab === 'jobs' ? 'active' : ''}`}
            onClick={() => setActiveTab('jobs')}
          >
            Job History
          </button>
          <button 
            className={`tab-button ${activeTab === 'transactions' ? 'active' : ''}`}
            onClick={() => setActiveTab('transactions')}
          >
            Transaction History
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'jobs' && (
            <div className="jobs-tab">
              <JobsTable 
                isAdminMode={false}
                showUserColumn={false}
                emptyStateTitle="No Jobs Found"
                emptyStateDescription="Your job history will appear here once you start running operations"
                maxHeight="600px"
              />
            </div>
          )}

          {activeTab === 'transactions' && (
            <div className="transactions-tab">
              <BillingHistory />
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showPaymentMethod && (
        <PaymentMethodModal 
          onClose={() => setShowPaymentMethod(false)}
        />
      )}

      {showAddFunds && (
        <AddFundsModal 
          onClose={() => setShowAddFunds(false)}
        />
      )}
    </div>
  );
};

export default AccountPage; 